import React, { useMemo, useState } from 'react';
import { useKanban } from '../context/KanbanContext';
import { FormularioRegistro } from './FormularioRegistro';
import { ModalEditarTarea } from './ModalEditarTarea';
import {
    TareaConRelaciones, EstadoTarea, COLUMNS_ORDER, ESTADO_LABELS,
    PRIORIDAD_LABELS, PRIORIDAD_COLORS, TIPO_TAREA_LABELS, TipoTarea, Prioridad
} from '../types';

const estadoHeaderColor: Record<EstadoTarea, string> = {
    backlog: 'bg-slate-500',
    por_hacer: 'bg-blue-500',
    en_progreso: 'bg-amber-500',
    esperando_cliente: 'bg-orange-500',
    testing: 'bg-purple-500',
    produccion: 'bg-emerald-500'
};

const tipoIcons: Record<string, string> = {
    bug_soporte: '🐛',
    nuevo_modulo: '🚀',
    actualizacion_refactor: '🔄'
};

const isOverdue = (fecha: string | null, estado: EstadoTarea): boolean => {
    if (!fecha || estado === 'produccion') return false;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return new Date(fecha) < today;
};

const formatFechaCorta = (iso: string | null): string => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' });
};

interface MiniTarjetaProps {
    tarea: TareaConRelaciones;
    onComplete: () => void;
    onEdit: () => void;
    completing: boolean;
}

const MiniTarjeta: React.FC<MiniTarjetaProps> = ({ tarea, onComplete, onEdit, completing }) => {
    const isCompleted = tarea.estado === 'produccion';
    const overdue = isOverdue(tarea.fecha_limite, tarea.estado);
    const prio = PRIORIDAD_COLORS[tarea.prioridad as Prioridad];

    return (
        <div
            className={`group relative rounded-lg p-2.5 mb-2 border transition-all cursor-pointer hover:shadow-md ${
                isCompleted
                    ? 'bg-emerald-50 border-emerald-300'
                    : overdue
                        ? 'bg-red-50/60 border-red-200'
                        : 'bg-white border-slate-200 hover:border-slate-300'
            }`}
            onClick={onEdit}
        >
            <div className="flex items-start gap-2 mb-1.5">
                <span
                    className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${prio.bg} ${prio.text}`}
                    title={`Prioridad: ${PRIORIDAD_LABELS[tarea.prioridad as Prioridad]}`}
                >
                    {prio.icon}
                </span>
                <p className={`text-xs font-medium leading-snug line-clamp-2 flex-1 ${
                    isCompleted ? 'line-through text-emerald-700' : 'text-slate-700'
                }`}>
                    {tarea.titulo}
                </p>
                {!isCompleted && (
                    <button
                        onClick={(e) => { e.stopPropagation(); onComplete(); }}
                        disabled={completing}
                        className="flex-shrink-0 w-5 h-5 rounded border-2 border-emerald-400 hover:bg-emerald-500 hover:border-emerald-500 hover:text-white text-emerald-500 flex items-center justify-center transition-all disabled:opacity-50"
                        title="Marcar como completada"
                    >
                        {completing ? (
                            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            </svg>
                        ) : (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </button>
                )}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
                <span className="text-slate-500">{tipoIcons[tarea.tipo_tarea]} {TIPO_TAREA_LABELS[tarea.tipo_tarea as TipoTarea].split(' ')[0]}</span>
                <span className={`px-1 py-0.5 rounded font-semibold ${tarea.tamanio === 'XL' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                    {tarea.tamanio}
                </span>
                {tarea.bloqueado && (
                    <span className="text-red-600 font-semibold">🚫</span>
                )}
                {tarea.fecha_limite && (
                    <span className={`ml-auto ${overdue ? 'text-red-600 font-semibold' : 'text-slate-500'}`}>
                        {overdue ? '⚠️' : '📅'} {formatFechaCorta(tarea.fecha_limite)}
                    </span>
                )}
            </div>
            {isCompleted && (
                <div className="absolute top-1 right-1 bg-emerald-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
            )}
        </div>
    );
};

interface ColumnaProps {
    estado: EstadoTarea;
    tareas: TareaConRelaciones[];
    onAdd: () => void;
    onComplete: (tareaId: number) => void;
    onEdit: (tarea: TareaConRelaciones) => void;
    completingId: number | null;
}

const Columna: React.FC<ColumnaProps> = ({ estado, tareas, onAdd, onComplete, onEdit, completingId }) => {
    const esProduccion = estado === 'produccion';
    return (
        <div className={`flex flex-col rounded-xl border ${esProduccion ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-white'} min-h-[400px]`}>
            <div className={`px-3 py-2.5 ${estadoHeaderColor[estado]} text-white rounded-t-xl flex items-center justify-between`}>
                <div className="flex items-center gap-2 min-w-0">
                    <h3 className="text-xs font-semibold uppercase tracking-wider truncate">
                        {ESTADO_LABELS[estado]}
                    </h3>
                    <span className="bg-white/20 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none">
                        {tareas.length}
                    </span>
                </div>
                <button
                    onClick={onAdd}
                    className="w-6 h-6 rounded-md bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                    title={`Agregar tarea en ${ESTADO_LABELS[estado]}`}
                >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                </button>
            </div>
            <div className="flex-1 p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                {tareas.length === 0 ? (
                    <div className="text-center py-6 text-xs text-slate-400 italic">
                        Sin tareas
                    </div>
                ) : (
                    tareas.map(t => (
                        <MiniTarjeta
                            key={t.id}
                            tarea={t}
                            onComplete={() => onComplete(t.id)}
                            onEdit={() => onEdit(t)}
                            completing={completingId === t.id}
                        />
                    ))
                )}
            </div>
        </div>
    );
};

export const VistaPorCliente: React.FC = () => {
    const { swimlanes, clientes, fetchKanbanBoard, marcarCompletado } = useKanban();
    const [clienteIdSeleccionado, setClienteIdSeleccionado] = useState<number | null>(null);
    const [showAddForEstado, setShowAddForEstado] = useState<EstadoTarea | null>(null);
    const [editTarea, setEditTarea] = useState<TareaConRelaciones | null>(null);
    const [completingId, setCompletingId] = useState<number | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const tareasDelCliente = useMemo(() => {
        if (!clienteIdSeleccionado) return [];
        const out: TareaConRelaciones[] = [];
        swimlanes.forEach(sw => {
            Object.values(sw.tareas).forEach(arr => {
                arr.forEach(t => {
                    if (t.cliente_id === clienteIdSeleccionado) out.push(t);
                });
            });
        });
        return out;
    }, [swimlanes, clienteIdSeleccionado]);

    const tareasPorEstado = useMemo(() => {
        const map: Record<EstadoTarea, TareaConRelaciones[]> = {
            backlog: [], por_hacer: [], en_progreso: [],
            esperando_cliente: [], testing: [], produccion: []
        };
        tareasDelCliente.forEach(t => {
            if (!t.archivado) map[t.estado].push(t);
        });
        return map;
    }, [tareasDelCliente]);

    const clienteActivo = clientes.find(c => c.id === clienteIdSeleccionado);
    const totalTareas = tareasDelCliente.length;
    const totalActivas = tareasDelCliente.filter(t => !t.archivado && t.estado !== 'produccion').length;
    const totalCompletadas = tareasDelCliente.filter(t => t.estado === 'produccion').length;
    const totalUrgentes = tareasDelCliente.filter(t => t.prioridad === 'urgente' && t.estado !== 'produccion').length;
    const totalVencidas = tareasDelCliente.filter(t => isOverdue(t.fecha_limite, t.estado)).length;

    const handleComplete = async (tareaId: number) => {
        setCompletingId(tareaId);
        setErrorMsg(null);
        const success = await marcarCompletado(tareaId);
        setCompletingId(null);
        if (!success) {
            setErrorMsg(`⛔ No se pudo completar. Verificá el límite WIP de Producción o reintentá.`);
            setTimeout(() => setErrorMsg(null), 5000);
        }
    };

    return (
        <div>
            <div className="mb-4 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-3">
                    <label className="text-sm font-medium text-slate-700">Cliente:</label>
                    <select
                        value={clienteIdSeleccionado ?? ''}
                        onChange={e => setClienteIdSeleccionado(e.target.value ? parseInt(e.target.value, 10) : null)}
                        className="flex-1 min-w-[220px] max-w-md px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                        <option value="">— Seleccionar cliente —</option>
                        {clientes.map(c => (
                            <option key={c.id} value={c.id}>
                                {c.empresa} — {c.nombre}
                            </option>
                        ))}
                    </select>

                    {clienteActivo && (
                        <div className="flex items-center gap-2 text-xs">
                            <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: clienteActivo.color }}
                            />
                            <span className="font-semibold text-slate-700">{clienteActivo.empresa}</span>
                        </div>
                    )}

                    {clienteIdSeleccionado && (
                        <div className="flex flex-wrap items-center gap-2 ml-auto text-xs">
                            <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded-md font-medium">
                                Total: {totalTareas}
                            </span>
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md font-medium">
                                Activas: {totalActivas}
                            </span>
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md font-medium">
                                ✓ Completadas: {totalCompletadas}
                            </span>
                            {totalUrgentes > 0 && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md font-medium">
                                    🔥 Urgentes: {totalUrgentes}
                                </span>
                            )}
                            {totalVencidas > 0 && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md font-medium">
                                    ⚠ Vencidas: {totalVencidas}
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {errorMsg && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                    {errorMsg}
                </div>
            )}

            {!clienteIdSeleccionado ? (
                <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
                    <div className="text-5xl mb-3">👥</div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-1">Seleccioná un cliente</h3>
                    <p className="text-sm text-slate-500">
                        Elegí un cliente del dropdown de arriba para ver sus tareas organizadas en las 6 columnas del flujo.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                    {COLUMNS_ORDER.map(estado => (
                        <Columna
                            key={estado}
                            estado={estado}
                            tareas={tareasPorEstado[estado]}
                            onAdd={() => setShowAddForEstado(estado)}
                            onComplete={handleComplete}
                            onEdit={setEditTarea}
                            completingId={completingId}
                        />
                    ))}
                </div>
            )}

            {showAddForEstado && clienteIdSeleccionado && (
                <FormularioRegistro
                    onClose={() => setShowAddForEstado(null)}
                    onSuccess={() => { setShowAddForEstado(null); fetchKanbanBoard(); }}
                    clienteIdInicial={clienteIdSeleccionado}
                    estadoInicial={showAddForEstado}
                />
            )}

            {editTarea && (
                <ModalEditarTarea
                    tarea={editTarea}
                    onClose={() => setEditTarea(null)}
                    onActualizar={async () => { setEditTarea(null); await fetchKanbanBoard(); return true; }}
                    onEliminar={async () => { setEditTarea(null); await fetchKanbanBoard(); return true; }}
                    onMarcarCompletado={async () => { setEditTarea(null); await fetchKanbanBoard(); return true; }}
                />
            )}
        </div>
    );
};